import {
	wrapper,
	createApiClient,
	Result,
	ApiError,
	PaginationParams,
} from "../base";
import { LaravelPaginatedCollection } from "@/types/collections";
import SegmentDO = App.Data.Segments.SegmentDO;
import SegmentIntrospectResultDTO = App.Data.Segments.SegmentIntrospectResultDTO;
import SegmentType = App.Enums.SegmentType;
import DocumentDO = App.Data.Explorer.DocumentDO;
import dayjs from "dayjs";
import ExportFileTypes = App.Enums.ExportFileTypes;

const baseClient = createApiClient();

type DimensionParams = PaginationParams & {
	sort?: string;
	"filter[type]"?: string | object;
	"filter[is_filterable]"?: boolean;
	"filter[is_configurable_on_axis]"?: boolean;
	"filter[domain]"?: string;
};

type VerticalParams = PaginationParams & {
	"filter[is_summarizable_in_segments]"?: boolean;
	"filter[include_hidden]"?: boolean;
};

type DownloadParams = PaginationParams & {
	date?: string;
	format: ExportFileTypes;
};

export default class {
	show(segmentUuid: string): Promise<Result<SegmentDO, ApiError>> {
		return wrapper(baseClient.get(`segments/${segmentUuid}`));
	}

	index(params: {
		page: number;
		itemsPerPage: number;
		sortBy: { key: string; order: "asc" | "desc" }[];
		search: string;
	}): Promise<Result<LaravelPaginatedCollection<SegmentDO>, ApiError>> {
		let sortBy = undefined;
		if (params.sortBy.length > 0) {
			sortBy =
				params.sortBy[0].order === "desc"
					? `-${params.sortBy[0].key}`
					: params.sortBy[0].key;
		}
		return wrapper(
			baseClient.get("segments", {
				params: {
					page: params.page,
					per_page: params.itemsPerPage,
					sort: sortBy,
					"filter[search]": params.search,
				},
			})
		);
	}

	indexSegments(params: {
		page: number;
		itemsPerPage: number;
		sortBy: { key: string; order: "asc" | "desc" }[];
	}) {
		return this.index(params);
	}

	create(
		segment: SegmentDO,
		dependsOnSegmentId: null | number = null,
		dependsOnSegmentDimensionNameClean:
			| "segmentcustomer"
			| "segmentlostcustomer"
			| null = null,
		cohortNoOfMonths: number = 12
	): Promise<Result<SegmentDO, ApiError>> {
		return wrapper(
			baseClient.post(`/segments`, {
				segment,
				dependsOnSegmentId,
				dependsOnSegmentDimensionNameClean,
				cohortNoOfMonths,
			})
		);
	}

	delete(id: string) {
		return wrapper<any>(baseClient.delete(`segments/${id}`));
	}

	update(segment: SegmentDO): Promise<Result<SegmentDO, ApiError>> {
		return wrapper(baseClient.put(`/segments/${segment.uuid}`, segment));
	}

	softUpdate(segment: SegmentDO): Promise<Result<null, ApiError>> {
		return wrapper(
			baseClient.put(`/segments/${segment.uuid}/soft-update`, segment)
		);
	}

	introspect(segment: SegmentDO | number) {
		if (typeof segment === "number") {
			return wrapper<SegmentIntrospectResultDTO[]>(
				baseClient.get(`segments/${segment}/introspect`)
			);
		}

		return wrapper<SegmentIntrospectResultDTO[]>(
			baseClient.post(`segments/introspect`, segment)
		);
	}

	size(segment: SegmentDO | number) {
		if (typeof segment === "number") {
			return wrapper<{ size: number }>(
				baseClient.get(`segments/${segment}/size`)
			);
		}

		return wrapper<{ size: number }>(baseClient.post(`segments/size`, segment));
	}

	totalBase(type: SegmentType) {
		return wrapper<{ count: number }>(
			baseClient.get("segments/total-base", {
				params: {
					type,
				},
			})
		);
	}

	downloadSegment(
		segment: SegmentDO,
		params: DownloadParams = { format: "csv" }
	): Promise<Result<string, ApiError>> {
		return wrapper(
			baseClient.post(`segments/download`, segment, {
				params,
				responseType: "blob",
			})
		);
	}

	getDimensions(
		params: DimensionParams = {}
	): Promise<Result<App.Data.DimensionDO[], ApiError>> {
		return wrapper(baseClient.get(`segments/get-dimensions`, { params }));
	}

	getVerticals(
		params: VerticalParams = {}
	): Promise<Result<App.Data.DocumentVerticalDTO[], ApiError>> {
		return wrapper(baseClient.get(`segments/get-verticals`, { params }));
	}

	getHierarchy(
		documentDO: DocumentDO | null = null,
		hierarchy_definition_id: number,
		excludedBranches: object = {},
		includeVerticalValues: boolean = false,
		signal?: AbortSignal
	): Promise<Result<[], ApiError>> {
		return wrapper(
			baseClient.post(
				`segments/get-hierarchy/${hierarchy_definition_id}`,
				{
					document: documentDO,
					excludedBranches,
					includeVerticalValues,
				},
				{ signal }
			)
		);
	}

	getValuesForDimensionAndSpecification(
		dimension_id: number,
		specification_id: number | null
	): Promise<Result<App.Data.Explorer.Filter.FilterValueDTO[], ApiError>> {
		return wrapper(
			baseClient.get(
				`segments/get-values-for-dimension-and-specification/${dimension_id}` +
					(specification_id ? "/" + specification_id : "")
			)
		);
	}

	getBuckets(): Promise<Result<App.Data.DimensionBucketDO[], ApiError>> {
		return wrapper(baseClient.get(`segments/get-buckets`));
	}

	getDocumentPeriodFilter(
		documentUuid: string
	): Promise<Result<App.Data.Explorer.Filter.ActiveFilterDTO | null, ApiError>> {
		return wrapper(
			baseClient.get(`segments/document/${documentUuid}/period-filter`)
		);
	}

	getDocumentForSegmentSizeInsights(
		segmentUuid: string,
		compareEnabled: boolean = false
	): Promise<Result<DocumentDO, ApiError>> {
		return wrapper(
			baseClient.get(
				`segments/insights/${segmentUuid}/get-document-for-segment-size`,
				{
					params: {
						compareEnabled,
					},
				}
			)
		);
	}

	getDocumentForSegmentRetentionInsights(
		segmentUuid: string,
		compareEnabled: boolean = false
	): Promise<Result<DocumentDO, ApiError>> {
		return wrapper(
			baseClient.get(
				`segments/insights/${segmentUuid}/get-document-for-segment-retention-12m`,
				{
					params: {
						compareEnabled,
					},
				}
			)
		);
	}

	getDocumentForSegmentRetentionCohortInsights(
		segmentUuid: string,
		cohortStartDate: string,
		cohortStartDateParsed: Date,
		cohortMonthsDisplayed: number,
		compareEnabled: boolean = false
	): Promise<Result<DocumentDO, ApiError>> {
		return wrapper(
			baseClient.get(
				`segments/insights/${segmentUuid}/get-document-for-segment-retention-single-cohort`,
				{
					params: {
						compareEnabled,
						cohortStartDate,
						cohortMonthsDisplayed,
						cohortStartDateParsed: dayjs(cohortStartDateParsed)
							.tz("Europe/Amsterdam")
							.format("YYYY-MM-DD HH:mm:ss"),
					},
				}
			)
		);
	}

	fetchDataForDocument(
		appDocument: DocumentDO,
		routePrefix: string = "/",
		signal?: AbortSignal
	): Promise<Result<[], ApiError>> {
		return wrapper(
			baseClient.post(`${routePrefix}fetchDataForDocument`, appDocument, {
				signal,
			})
		);
	}
}
